import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, TemplateRef, ViewChild } from '@angular/core';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { MatTableDataSource } from '@angular/material/table';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { Customer, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { EnergySystem, LysineSystem } from '../../../settings/models/settings.model';
import {
  getEnergySetting,
  getLysineSetting,
} from '../../../settings/store/selectors/settings.selectors';
import { REGIONS } from '../../../shared/models/regions';
import { Product, ViewMode } from '../../../shared/models/shared.model';
import * as actions from '../../store/actions/product.actions';
import { upsertProductRequest } from '../../store/actions/product.actions';
import { getCustomerProducts, getProducts } from '../../store/selectors/product.selectors';
import { getSelectedProductCountry } from '../../store/selectors/product.selectors';
import { ProductDetailComponent } from '../product-detail/product-detail.component';

@Component({
  selector: 'app-product-overview',
  templateUrl: './product-overview.component.html',
  styleUrls: ['./product-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    NgIf,
    MatSelect,
    NgFor,
    MatOption,
    TableComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class ProductOverviewComponent {
  pageTitle$: Observable<string> = this.store
    .select(RouterSelectors.getPath)
    .pipe(
      map((route) =>
        this.localeService.translate(
          `product-overview.${
            route.indexOf('/customer-product') !== -1 ? 'customer-title' : 'title'
          }`,
        ),
      ),
    );
  customer$: Observable<Customer> = this.store.select(UserSelectors.selectCustomer).pipe(
    skipWhile((c) => !c),
    take(1),
  );
  products$: Observable<Product[]> = this.store.select(getProducts);
  customerProducts$: Observable<Product[]> = this.store.select(getCustomerProducts);
  selectedRegion$ = this.store.select(getSelectedProductCountry);
  selectedLocation$ = this.store.select(UserSelectors.selectLocation).pipe(
    skipWhile((c) => !c),
    take(1),
  );
  isCustomerProducts$ = this.store
    .select(RouterSelectors.getPath)
    .pipe(map((urlPath) => urlPath.indexOf('/customer-product') !== -1));
  energySetting$: Observable<EnergySystem> = this.store
    .select(getEnergySetting)
    .pipe(skipWhile((s) => !s));
  lysineSetting$: Observable<LysineSystem> = this.store
    .select(getLysineSetting)
    .pipe(skipWhile((s) => !s));

  allowedToCUD$: Observable<boolean> = this.selectedRegion$.pipe(
    withLatestFrom(
      this.selectedLocation$,
      this.store.select(AuthorizationSelectors.hasAdministratorRole),
    ),
    map(
      ([region, location, hasAdministratorRole]) =>
        (region === location.country && hasAdministratorRole) || !hasAdministratorRole,
    ),
  );

  customerId: number;
  region: string;
  regions: Array<{ id: string; value: string }> = REGIONS.sort((a, b) =>
    a.value < b.value ? -1 : 1,
  );

  @ViewChild('nameTemplate') nameTemplate: TemplateRef<void>;

  private readonly tableConfigBase: TableConfig = {
    dataSource: undefined,
    columns: undefined,
    selectColumn: false,
    actionColumn: true,
    pagination: false,
    disableCards: true,
    footer: false,
    scrolling: true,
    wrapperWidth: '100%',
  };

  public tableConfig$: Observable<TableConfig> = combineLatest([
    this.store.select(RouterSelectors.getPath).pipe(take(1)),
    this.customer$,
    this.products$,
    this.customerProducts$,
    this.isCustomerProducts$,
    this.selectedRegion$,
    this.energySetting$,
    this.lysineSetting$,
    this.allowedToCUD$.pipe(take(1)),
  ]).pipe(
    map(
      ([
        route,
        customer,
        products,
        customerProducts,
        isCustomerProducts,
        selectedProductCountry,
        energy,
        lysine,
        allowedToCUD,
      ]: [string, Customer, Product[], Product[], boolean, string, string, string, boolean]) => {
        let productList: Product[] = [];
        if (!!customer && !!customerProducts && route.indexOf('/customer-product') !== -1) {
          productList = customerProducts;
          this.customerId = customer.id;
        } else if (!!products && route.indexOf('/product') !== -1) {
          productList = products;
          this.customerId = null;
        }

        if (!productList) {
          return undefined;
        }

        const tableConfig: TableConfig = {
          ...this.tableConfigBase,
          columns: [
            {
              id: 'name',
              type: TableColumnTypes.TEMPLATE,
              template: this.nameTemplate,
              label: this.localeService.translate('product-overview.overview.productName'),
            },
            {
              id: 'code',
              type: TableColumnTypes.STRING,
              label: this.localeService.translate('product-overview.overview.productCode'),
            },
            {
              id: 'phases',
              type: TableColumnTypes.STRING,
              label: this.localeService.translate('product-overview.overview.suitablePhases'),
            },
            {
              id: 'energyValue',
              type: TableColumnTypes.NUMBER,
              label: `${this.localeService.translate(
                'product-overview.overview.energy',
              )} (${this.localeService.translate('settings.energy.short.' + energy)})`,
            },
            {
              id: 'lysine',
              type: TableColumnTypes.NUMBER,
              label: `${this.localeService.translate(
                'product-overview.overview.lysine',
              )} (${this.localeService.translate('settings.lysine.short.' + lysine)})`,
            },
            {
              id: 'grossPrice',
              type: TableColumnTypes.NUMBER,
              label: this.localeService.translate('product-overview.overview.grossPrice'),
            },
            {
              id: 'phosphorus',
              type: TableColumnTypes.NUMBER,
              label: this.localeService.translate('product-overview.overview.phosphorus'),
            },
            {
              id: 'protein',
              type: TableColumnTypes.NUMBER,
              label: this.localeService.translate('product-overview.overview.protein'),
            },
          ],
          actions: [
            {
              id: 'edit',
              callback: (row: Product) => this.openProductEdit(row.id),
              label: 'Edit',
              icon: 'edit',
              disabled: () => !allowedToCUD,
              primary: false,
            },
            {
              id: 'delete',
              callback: (row: Product) => this.deleteProduct(row),
              label: 'Delete',
              icon: 'delete',
              disabled: () => !allowedToCUD,
              primary: false,
            },
          ],
        };

        if (isCustomerProducts && !!customer) {
          this.region = customer.country;
        } else {
          this.region = selectedProductCountry;
        }

        tableConfig.dataSource = new MatTableDataSource(
          productList.map((product) => {
            const dataObject: { [k: string]: unknown } = {
              id: product.id,
              name: product.name,
              code: product.code,
              phases: product.suitablePhases,
              energyValue: Math.round((product.ewKg + Number.EPSILON) * 100) / 100,
              lysine: Math.round((product.lysKg + Number.EPSILON) * 100) / 100,
              grossPrice: product.grossPricePer100Kg,
              phosphorus: product.phosphorusGramPerKg,
              protein: product.proteinGramPerKg,
              customerId: product.customerId,
            };
            return dataObject;
          }),
        );
        return tableConfig;
      },
    ),
  );

  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);

  constructor(
    private readonly store: Store<RootState>,
    private readonly localeService: LocaleService,
    private readonly dialog: DialogService,
  ) {}

  openProductView(productId: number) {
    this.store.dispatch(
      actions.setSelectedProductRequest({
        id: productId,
      }),
    );
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: false,
        productId,
        customerId: this.customerId,
        country: this.region,
        viewMode: ViewMode.VIEW,
      })
      .subscribe();
  }

  openProductCreate() {
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: false,
        productId: null,
        customerId: this.customerId,
        country: this.region,
        viewMode: ViewMode.CREATE,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(upsertProductRequest({ request: result.result.product }));
        }
      });
  }

  openProductEdit(productId: number) {
    this.store.dispatch(
      actions.setSelectedProductRequest({
        id: productId,
      }),
    );
    this.dialog
      .form(ProductDetailComponent, {
        setPendingChanges: false,
        productId,
        customerId: this.customerId,
        country: this.region,
        viewMode: ViewMode.EDIT,
      })
      .subscribe((result) => {
        if (result.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(upsertProductRequest({ request: result.result.product }));
        }
      });
  }

  deleteProduct(product: Product) {
    this.store.dispatch(actions.deleteProductWithConfirmation({ product }));
  }

  changeRegionSelection(selection: MatSelectChange) {
    this.store.dispatch(actions.setSelectedCountry({ save: true, id: selection.value as string }));
  }
}
